import { StackScreenProps } from "@react-navigation/stack";
import { ProductActionsRootParamList } from "../../../../types";
import { useScheduler } from "../../../../utilities/scheduler";
import { QUERY_PRODUCT_LIST } from "../../utils";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { useSearch } from "../../../../utilities/search";
import React from "react";
import {
  DEFAULT_PAGE_WIDTH,
  Divider,
  FlatListSearchable,
  Page,
  Text,
} from "@towersystems/roam-common-native";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { Spacings, View } from "react-native-ui-lib";
import { useFocusEffect } from "@react-navigation/native";
import { MainProductListItem } from "../../components/MainProductListItem";

export type ProductActionsSelectParentProductScreenProps = StackScreenProps<
  ProductActionsRootParamList,
  "ProductActionsSelectParentProductScreen"
>;

export const ProductActionsSelectParentProductScreen = ({
  navigation,
}: ProductActionsSelectParentProductScreenProps) => {
  const { schedule } = useScheduler(500);
  const search = useSearch();

  const loadMoreList = useLoadMoreList<Product>({
    initialLoad: false,
    query: QUERY_PRODUCT_LIST,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        filterOperator: "AND",
        options: variables,
      };
    },
  });

  React.useEffect(() => {
    if (search.touched) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query, search.touched]);

  useFocusEffect(
    React.useCallback(() => {
      loadMoreList.reset();
      handleLoadMore(true);
    }, [])
  );

  const handleLoadMore = React.useCallback(
    (reset?: boolean) => {
      loadMoreList.loadMore(
        {
          filter: {
            ...(search.query.length > 0 && {
              term: {
                contains: search.query,
              },
            }),
            mainProduct: {
              eq: true,
            },
          },
        },
        reset
      );
    },
    [search.query, loadMoreList.loadMore]
  );

  const items: Product[] = loadMoreList.items || [];

  const emptyComponentMarkup = (
    <View>
      <Text>No products to show. Try searching.</Text>
    </View>
  );

  const renderItem = React.useCallback(({ item }: { item: Product }) => {
    return (
      <MainProductListItem
        onPress={() =>
          navigation.navigate("ProductActionsSelectParentOptionValuesScreen", {
            productId: item.id,
          })
        }
        product={item}
      />
    );
  }, []);

  if (loadMoreList.loading && loadMoreList.called == false) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />;
      </Page>
    );
  }

  return (
    <Page scrollable={false} padding={0} fullWidth>
      <FlatListSearchable
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: Spacings.s5,
          paddingTop: Spacings.s5,
        }}
        placeholder="Search..."
        onChange={(query) => search.setQuery(query)}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        refreshing={loadMoreList.loading}
        data={items as any}
        scrollEnabled={true}
        ListEmptyComponent={emptyComponentMarkup}
        onEndReached={() => items.length >= 1 && handleLoadMore(false)}
      />
    </Page>
  );
};
